import { useEffect } from 'react';
import { useACLStore } from '@/stores/useACLStore';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

const roles = ['owner', 'admin', 'analyst', 'agent', 'viewer', 'buyer'];

export const PermissionMatrix = () => {
  const { permissions, rolePermissions, loading, loadPermissions, loadRolePermissions, toggleRolePermission } = useACLStore();

  useEffect(() => {
    loadPermissions();
    loadRolePermissions();
  }, [loadPermissions, loadRolePermissions]);

  const hasPermission = (role: string, permissionKey: string): boolean => {
    return rolePermissions.some(
      (rp) => rp.role === role && rp.permission_key === permissionKey
    );
  };

  const handleToggle = (role: string, permissionKey: string, enabled: boolean) => {
    toggleRolePermission(role, permissionKey, enabled);
  };

  if (loading && permissions.length === 0) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-10">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Permission Matrix</CardTitle>
        <CardDescription>
          Manage which roles have access to specific permissions
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[300px]">Permission</TableHead>
                {roles.map((role) => (
                  <TableHead key={role} className="text-center">
                    <Badge variant="outline" className="capitalize">
                      {role}
                    </Badge>
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {permissions.map((permission) => (
                <TableRow key={permission.key}>
                  <TableCell>
                    <div>
                      <div className="font-medium">{permission.key}</div>
                      {permission.description && (
                        <div className="text-sm text-muted-foreground">
                          {permission.description}
                        </div>
                      )}
                    </div>
                  </TableCell>
                  {roles.map((role) => (
                    <TableCell key={`${role}-${permission.key}`} className="text-center">
                      <Switch
                        checked={hasPermission(role, permission.key)}
                        onCheckedChange={(checked) =>
                          handleToggle(role, permission.key, checked)
                        }
                        disabled={loading}
                      />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
};
